import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Check, Package, Truck, Sparkles, ExternalLink, RotateCcw } from "lucide-react";
import { api, formatPrice } from "../lib/api";
import Seo from "../components/Seo";

const STAGES = [
  { key: "confirmed", label: "Confirmed", icon: Check },
  { key: "processing", label: "Processing", icon: Package },
  { key: "shipped", label: "Shipped", icon: Truck },
  { key: "delivered", label: "Delivered", icon: Sparkles },
];

function stageIndex(status) {
  const s = (status || "").toLowerCase();
  if (s === "delivered") return 3;
  if (s === "shipped") return 2;
  if (s === "processing") return 1;
  return 0; // confirmed / paid / pending
}

export default function OrderTrack() {
  const [params, setParams] = useSearchParams();
  const [orderNumber, setOrderNumber] = useState(params.get("o") || "");
  const [email, setEmail] = useState(params.get("e") || "");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const lookup = async (e) => {
    if (e) e.preventDefault();
    setError("");
    if (!orderNumber.trim() || !email.trim()) {
      setError("Please enter both order number and email.");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/orders/track", {
        order_number: orderNumber.trim(),
        email: email.trim(),
      });
      setOrder(r.data);
      // Persist a shareable, bookmarkable URL.
      setParams({ o: orderNumber.trim(), e: email.trim() }, { replace: true });
    } catch (err) {
      setOrder(null);
      const msg = err?.response?.data?.detail || "Could not find your order.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Auto-lookup if query params are present on first load (shareable links).
  useEffect(() => {
    if (params.get("o") && params.get("e") && !order && !loading) {
      lookup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refunded = !!order?.refunded_at;
  const currentStage = order ? stageIndex(order.status) : -1;

  return (
    <div className="pt-32 pb-24" data-testid="order-track-page">
      <Seo
        title="Track your order — Elegant Affairs"
        description="Track the status of your Elegant Affairs order with your order number and email."
      />
      <div className="max-w-[1100px] mx-auto px-6 md:px-12">
        <p className="eyebrow">Post-purchase</p>
        <h1 className="font-display text-5xl md:text-7xl mt-4 leading-[0.95] tracking-tighter">
          Track your <em className="font-medium italic">order.</em>
        </h1>
        <p className="mt-6 text-[var(--mn-ink-2)] max-w-xl leading-relaxed">
          Enter the order number from your confirmation email plus the email
          address you used at checkout to see live status, shipping details, and
          tracking links.
        </p>

        {/* Lookup form */}
        <form
          onSubmit={lookup}
          className="mt-12 border border-[var(--mn-ink)] p-6 md:p-10 grid grid-cols-1 md:grid-cols-[1.2fr_1.4fr_auto] gap-4 md:gap-6 items-end"
          data-testid="track-form"
        >
          <label className="block">
            <span className="eyebrow block mb-2">Order Nº</span>
            <input
              type="text"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              placeholder="EA-XXXXXXXX"
              className="field-input font-mono-luxe"
              data-testid="track-order-number"
              autoComplete="off"
            />
          </label>
          <label className="block">
            <span className="eyebrow block mb-2">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="field-input"
              data-testid="track-email"
              autoComplete="email"
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary !py-4"
            data-testid="track-submit"
          >
            <Search size={14} strokeWidth={1.5} />
            {loading ? "Looking up…" : "Track order"}
          </button>
        </form>

        {error && (
          <p
            className="mt-6 text-sm text-[var(--mn-accent)] font-medium"
            data-testid="track-error"
          >
            {error}
          </p>
        )}

        {/* Order result */}
        {order && (
          <div className="mt-16 border border-[var(--mn-ink)] p-8 md:p-14" data-testid="track-result">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-sm border-b border-[var(--mn-border)] pb-8">
              <div>
                <p className="eyebrow">Order Nº</p>
                <p className="font-mono-luxe mt-2" data-testid="result-order-number">{order.order_number}</p>
              </div>
              <div>
                <p className="eyebrow">Placed</p>
                <p className="font-mono-luxe mt-2">
                  {new Date(order.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                </p>
              </div>
              <div>
                <p className="eyebrow">Total</p>
                <p className="font-mono-luxe mt-2">{formatPrice(order.total)}</p>
              </div>
              <div>
                <p className="eyebrow">Status</p>
                <p className="font-mono-luxe mt-2 capitalize" data-testid="result-status">
                  {refunded ? "Refunded" : order.status}
                </p>
              </div>
            </div>

            {/* Progress tracker */}
            {!refunded && (
              <div className="mt-10" data-testid="track-progress">
                <p className="eyebrow mb-6">Progress</p>
                <ol className="grid grid-cols-4 gap-2 md:gap-4">
                  {STAGES.map((stage, idx) => {
                    const Icon = stage.icon;
                    const done = idx <= currentStage;
                    const active = idx === currentStage;
                    return (
                      <li
                        key={stage.key}
                        className="flex flex-col items-center text-center relative"
                        data-testid={`stage-${stage.key}`}
                      >
                        <div
                          className={`w-12 h-12 md:w-14 md:h-14 flex items-center justify-center border-2 ${
                            done
                              ? "border-[var(--mn-ink)] bg-[var(--mn-ink)] text-[var(--mn-bg)]"
                              : "border-[var(--mn-border-strong)] text-[var(--mn-ink-2)]"
                          } ${active ? "ring-2 ring-offset-2 ring-[var(--mn-accent)]" : ""}`}
                        >
                          <Icon size={18} strokeWidth={1.5} />
                        </div>
                        <span
                          className={`text-[10px] md:text-xs uppercase tracking-[0.18em] mt-3 ${
                            done ? "text-[var(--mn-ink)]" : "text-[var(--mn-ink-2)]"
                          }`}
                        >
                          {stage.label}
                        </span>
                        {idx < STAGES.length - 1 && (
                          <span
                            className={`absolute top-6 md:top-7 left-[60%] right-[-40%] h-px ${
                              idx < currentStage ? "bg-[var(--mn-ink)]" : "bg-[var(--mn-border-strong)]"
                            }`}
                          />
                        )}
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}

            {/* Refund notice */}
            {refunded && (
              <div className="mt-10 border border-[var(--mn-accent)] p-6 flex items-start gap-4" data-testid="track-refund-notice">
                <RotateCcw size={22} strokeWidth={1.4} className="text-[var(--mn-accent)] shrink-0 mt-1" />
                <div>
                  <p className="eyebrow text-[var(--mn-accent)]">Refunded</p>
                  <p className="font-display text-2xl mt-1">
                    {formatPrice(order.refund_amount || order.total)} refunded
                  </p>
                  <p className="text-sm text-[var(--mn-ink-2)] mt-2">
                    Funds typically appear on your original payment method within 5–10 business days. Need help?{" "}
                    <a className="underline" href="mailto:concierge@elegantaffairs.shop">Contact us</a>.
                  </p>
                </div>
              </div>
            )}

            {/* Shipping / tracking block */}
            {order.tracking_number && (
              <div className="mt-10 bg-[var(--mn-ink)] text-[var(--mn-bg)] p-6 md:p-8" data-testid="track-shipping-block">
                <p className="eyebrow text-white/60">In transit</p>
                <div className="mt-3 flex flex-wrap items-end justify-between gap-6">
                  <div>
                    <p className="font-display text-2xl md:text-3xl tracking-tight">
                      {order.tracking_carrier || "Carrier"} · {order.tracking_number}
                    </p>
                    {order.shipped_at && (
                      <p className="text-xs text-white/60 mt-1 uppercase tracking-[0.22em]">
                        Shipped {new Date(order.shipped_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </p>
                    )}
                  </div>
                  {order.tracking_url && (
                    <a
                      href={order.tracking_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] border border-white px-5 py-3 hover:bg-white hover:text-[var(--mn-ink)] transition-colors"
                      data-testid="track-carrier-link"
                    >
                      Track on {order.tracking_carrier || "carrier"} <ExternalLink size={12} strokeWidth={1.5} />
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Items + ship-to */}
            <div className="mt-12 grid grid-cols-12 gap-8">
              <div className="col-span-12 md:col-span-7">
                <p className="eyebrow mb-4">Items</p>
                <ul className="divide-y divide-[var(--mn-border)] border-y border-[var(--mn-border)]">
                  {order.items.map((it) => (
                    <li key={it.product_id} className="py-4 flex gap-4 items-center" data-testid="track-item">
                      <div className="w-14 h-16 bg-neutral-100 overflow-hidden shrink-0">
                        {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-display text-lg leading-tight truncate">{it.name}</p>
                        <p className="text-xs text-[var(--mn-ink-2)] mt-1">Qty {it.quantity}</p>
                      </div>
                      <p className="font-mono-luxe text-sm shrink-0">
                        {formatPrice((it.price || 0) * (it.quantity || 1))}
                      </p>
                    </li>
                  ))}
                </ul>

                <div className="mt-6 space-y-2 text-sm">
                  <Row label="Subtotal" value={formatPrice(order.subtotal)} />
                  <Row label="Shipping" value={order.shipping_total > 0 ? formatPrice(order.shipping_total) : "Free"} />
                  {order.tax_total > 0 && <Row label="Tax" value={formatPrice(order.tax_total)} />}
                  <div className="border-t border-[var(--mn-border)] pt-3 flex justify-between items-baseline">
                    <span className="eyebrow">Total</span>
                    <span className="font-mono-luxe text-xl">{formatPrice(order.total)}</span>
                  </div>
                </div>
              </div>

              <div className="col-span-12 md:col-span-5">
                <p className="eyebrow mb-4">Shipping to</p>
                <address className="not-italic text-sm leading-relaxed text-[var(--mn-ink-2)]">
                  {order.shipping?.name}<br />
                  {order.shipping?.city}{order.shipping?.state ? `, ${order.shipping.state}` : ""}<br />
                  {order.shipping?.country}
                </address>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--mn-ink-2)] mt-6">
                  Need help with this order?
                </p>
                <a
                  href={`mailto:concierge@elegantaffairs.shop?subject=Help%20with%20${order.order_number}`}
                  className="font-display text-2xl underline underline-offset-4 decoration-1 mt-2 inline-block"
                  data-testid="track-support-link"
                >
                  Contact concierge
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-[var(--mn-ink-2)]">{label}</span>
      <span className="font-mono-luxe">{value}</span>
    </div>
  );
}
